import { z } from 'zod';

/**
 * Canvas Node Type Schemas
 * Single source of truth for all node types and related enums
 */

// Base node categories (file-based vs AI-powered)
export const NodeCategorySchema = z.enum(['file', 'ai']);

// All available node types
export const NodeTypeSchema = z.enum([
  // File nodes
  'text-file',
  'image-file',
  'video-file',
  'audio-file',

  // AI nodes
  'text',
  'image',
  'video',
  '3d',
  'shader',

  // Shared Art / Node connections
  'frame-input',
  'frame-output',

  // Annotation nodes
  'comment',

  // Data / Series nodes
  'data-table',
  'variation-generator',
  'prompt-composer',
  'batch-generator',
  'gallery-group',
  'gallery-container',

  // Tool nodes
  'crop',
  'mask',
  'upscale',
  'outpaint',
  'controlnet',
  'levels',
  'blur',
  'background-remover',
  'lip-sync',
  'describer',
  'extract-video-frame',

  // Model asset nodes
  'lora-model'
]);

// Field types for node configuration
export const NodeFieldTypeSchema = z.enum([
  'prompt',
  'image',
  'video',
  'audio',
  'file',
  'select',
  'number'
]);

// Node execution status
export const NodeStatusSchema = z.enum([
  'idle',
  'queued',
  'working',
  'error',
  'success'
]);

// TypeScript types exported from schemas
export type NodeCategory = z.infer<typeof NodeCategorySchema>;
export type NodeType = z.infer<typeof NodeTypeSchema>;
export type NodeFieldType = z.infer<typeof NodeFieldTypeSchema>;
export type NodeStatus = z.infer<typeof NodeStatusSchema>;

// Utility: Check if node type is AI-powered
export function isAINode(type: NodeType): boolean {
  return !type.endsWith('-file') && type !== 'frame-input' && type !== 'frame-output';
}

// Utility: Check if node type is file-based
export function isFileNode(type: NodeType): boolean {
  return type.endsWith('-file');
}
